export default function SlidellTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Slidell &amp; Northshore Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Slidell and St. Tammany Parish have one of the most distinctive travel patterns in Louisiana — a suburban professional demographic that regularly accesses New Orleans International Airport but wants to avoid the NOLA traffic and passport agency lines that go with it.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">New Orleans Cruises — Caribbean &amp; Bahamas</h3>
                <p className="text-gray-400 text-xs">~35 miles to Port of New Orleans</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Port of New Orleans is just 35 miles from Slidell — making it one of the most convenient cruise departure points for Northshore families. Year-round sailings to the Caribbean and Bahamas are extremely popular with St. Tammany Parish residents. Families often book during promotional sale windows with 2–4 week lead times, then discover a child's or spouse's passport has expired. Holiday season cruises — Thanksgiving, Christmas, Mardi Gras — are especially common urgency triggers for Northshore families.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Caribbean cruises departing from New Orleans require passports for all passengers. Even closed-loop sailings that return to New Orleans require passports if any passenger boards or disembarks at a non-U.S. port or needs to fly home.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MSY — Louis Armstrong New Orleans International</h3>
                <p className="text-gray-400 text-xs">~40 miles from Slidell via I-10</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Louis Armstrong International Airport is approximately 40 miles from Slidell. Northshore professionals — attorneys, physicians, energy executives, consultants — fly through MSY regularly for international business travel to London, Mexico City, Toronto, and Cancun. Nonstop and connecting international service means Slidell residents are genuinely global business travelers who may have last-minute trip changes that require expedited passport processing.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking any international travel from MSY. Most countries require 6 months validity beyond your return date. With MSY serving international hubs directly, a last-minute booking can create an urgent passport situation.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-briefcase-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">New Orleans Commuter Professional Travel</h3>
                <p className="text-gray-400 text-xs">Energy, healthcare &amp; legal sectors</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Many Slidell residents commute to New Orleans for work in energy, healthcare, law, and finance. These professionals take international business trips for client meetings, regulatory compliance, and trade shows — often on short notice. An oil executive who books a flight to London or a healthcare administrator flying to a European conference at two weeks' notice may discover their passport is about to expire. We handle these business traveler urgencies regularly for Northshore professionals.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Business professionals should renew passports when they hit 18 months remaining — not when they expire. An international trip on short notice with an expiring passport is the most common urgency situation we see from Northshore commuters.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">SLU &amp; Study Abroad Travel</h3>
                <p className="text-gray-400 text-xs">Southeastern Louisiana University — Hammond</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Many Northshore students attend Southeastern Louisiana University in Hammond (about 40 miles from Slidell) or commute to Tulane and Loyola in New Orleans. Study-abroad programs in France, Spain, Italy, and Costa Rica are popular among Northshore students. First-time passport applications require an in-person acceptance clerk visit — a step that many college-age applicants do not discover until the week before they need their passport for an international program.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> SLU and Tulane study-abroad acceptance letters often arrive 6–8 weeks before program orientation. Start your passport process the day you receive acceptance — not the week before you fly.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-road-map-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Skip the Causeway — Serve Your Passport from Slidell</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              The Lake Pontchartrain Causeway is 23.87 miles long — the world's longest bridge over water. Northshore residents cross it frequently, but for a passport agency appointment in downtown New Orleans, it adds 45–60 minutes of drive time each way plus downtown parking. Fast Passport Center eliminates that trip entirely: start online in Slidell, ship your documents from Slidell, and we handle everything through reserved agency access.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}